import { useEffect, useState } from 'react';
import {
    Grid,
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { useFormik } from 'formik';
import { asyncGetDocumentTypes } from '../../../services/document-type.service';
import { validationschat, valuesChat } from './formikData';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { IDocumentType } from '../../../shared/types/document-type';
import { IFormHome } from '../../../shared/types/home';
import { AuthMessages } from '../../../shared/messages/authMessages';
import { ToastSwal } from '../../components/general/toastSwals/toastSwal';
import { ISelectType } from '../../../shared/types/select-type';
import { asyncGetCountries } from '../../../services/countries.service';
import { asyncGetDepartments } from '../../../services/departments.service';
import { asyncGetCities } from '../../../services/cities.service';
import { asyncGetRequestTypes } from '../../../services/request-type.service';
import { asyncGetRequestClassifications } from '../../../services/request-classification.service';
import { getBase64 } from '../../../shared/types/files';
import { asyncSavePQR } from '../../../services/pqr.service';
import { Form } from './form';
import { MsgCreation } from './msgCreation';
import { AnonymousDialog } from './anonymousDialog';

const home = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [identifications, setIdentifications] = useState<IDocumentType[]>([]);
    const [countries, setCountries] = useState<ISelectType[]>([]);
    const [departments, setDepartments] = useState<ISelectType[]>([]);
    const [cities, setCities] = useState<ISelectType[]>([]);
    const [requestTypes, setRequestTypes] = useState<ISelectType[]>([]);
    const [requestClassifications, setRequestClassifications] = useState<
        ISelectType[]
    >([]);
    const [load, setLoad] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [dialogData, setDialogData] = useState<any>();

    const [isAnonymous, setIsAnonymous] = useState(false);
    const [showAnonymousDialog, setShowAnonymousDialog] = useState(false);

    const handleChangeAnonymous = () => {
        if (!isAnonymous === true) setShowAnonymousDialog(true);
        else setIsAnonymous(!isAnonymous);
    };

    const handleAceptAnonymousDialog = () => {
        setIsAnonymous(true);
        setShowAnonymousDialog(false);
    };

    const handleCloseAnonymousDialog = () => {
        setIsAnonymous(false);
        setShowAnonymousDialog(false);
    };

    const getReCaptcha = async () => {
        try {
            if (executeRecaptcha) {
                const localToken = await executeRecaptcha('someAction');
                return localToken;
            }
        } catch (error) {
            console.error('Error al obtener reCAPTCHA:', error);
            return null;
        }
    };

    const save = async (values: IFormHome) => {
        setLoad(true);
        const data = {
            documentType: values.documentType,
            document: values.document,
            fullName: values.fullName,
            address: values.address,
            phone: values.phone,
            city: values.city,
            email: values.email,
            requestType: values.requestType,
            requestClassification: values.requestClassification,
            description: values.description,
            file: [],
            fileName: [],
        } as any;

        if (formik.values.files && formik.values.files.length > 0) {
            formik.values.files.forEach((f: any) => {
                data.fileName.push(f.fileName);
                data.file.push(f.base64);
            });
        }

        try {
            const res = await asyncSavePQR({
                ...data,
                isAnonymous,
            });
            setDialogData({ ...res, isCorrect: true });
        } catch (error) {
            setDialogData({ isCorrect: false });
        }
        formik.resetForm();
        setLoad(false);
    };

    const isFormValid = () => {
        return (
            formik.dirty &&
            formik.isValid &&
            Object.keys(formik.errors).length === 0
        );
    };

    const formik = useFormik({
        initialValues: valuesChat,
        validationSchema: validationschat(isAnonymous),
        onSubmit: async (formValues) => {
            const captcha = (await getReCaptcha()) || '';

            if (!captcha) {
                ToastSwal.fire({
                    text: AuthMessages.login['AUTH-NO-CAPTCHA'],
                    icon: 'error',
                    iconColor: '#1d1d1d',
                });
            } else {
                save(formValues);
            }
        },
    });

    const getIdentifications = async () => {
        const items = await asyncGetDocumentTypes();
        setIdentifications(items.map(({ id, name }: any) => ({ id, name })));
    };

    const getCountries = async () => {
        const items = await asyncGetCountries();
        setCountries(items.map(({ id, name }: any) => ({ id, name })));
    };

    const getDepartments = async () => {
        if (!Boolean(formik.values.country)) setDepartments([]);
        else {
            const items = await asyncGetDepartments(formik.values.country);
            setDepartments(items.map(({ id, name }: any) => ({ id, name })));
        }
        formik.values.department = '';
        formik.values.city = '';
    };

    const getCities = async () => {
        if (!Boolean(formik.values.department)) setCities([]);
        else {
            const items = await asyncGetCities(formik.values.department);
            setCities(items.map(({ id, name }: any) => ({ id, name })));
        }
        formik.values.city = '';
    };

    const getRequestTypes = async () => {
        const items = await asyncGetRequestTypes();
        setRequestTypes(items);
    };

    const requestTypesFiltered = isAnonymous ? requestTypes.filter(item => Boolean(item.showInAnonymous)) : requestTypes;

    const isComplaint = requestTypesFiltered.find(item => formik.values.requestType === item.id)?.code === 'QJA';
    const isDenunciation = requestTypesFiltered.find(item => formik.values.requestType === item.id)?.code === 'DNC';

    const requestClassificationsFiltered = isComplaint
        ? requestClassifications.filter(item => item.code === 'AIDFM')
        : (
            isDenunciation
                ? requestClassifications.filter(item => item.code === 'AIDADEFFM')
                : requestClassifications.filter(item => item.code !== 'AIDFM' && item.code !== 'AIDADEFFM')
        )

    const getRequestClassifications = async () => {
        const items = await asyncGetRequestClassifications();
        setRequestClassifications(items);
    };

    const onChangeFiles = async (value: any[]) => {
        const files: any[] = [];
        let filesSize = 0;
        for (let file of value as File[]) {
            if ((file as any)?.file) file = (file as any).file;
            files.push({
                fileName: file.name,
                base64: await getBase64(file),
            });
            filesSize += file.size;
        }
        formik.setFieldValue('files', files);
        formik.setFieldValue('filesSize', filesSize / 1024000);
    };

    useEffect(() => {
        getIdentifications();
        getCountries();
        getRequestTypes();
        getRequestClassifications();
    }, []);

    useEffect(() => {
        getDepartments();
    }, [formik.values.country]);

    useEffect(() => {
        getCities();
    }, [formik.values.department]);

    if (Boolean(dialogData)) return <MsgCreation dialogData={dialogData} />;

    return (
        <Box
            mt={2}
            mb={20}
            padding=".4rem 1rem"
            margin="0 auto"
            maxWidth="1100px"
        >
            <Grid style={{ display: Boolean(dialogData) ? 'none' : 'block' }}>
                <Typography fontSize="1.2rem">
                    Registrar Solicitud Peticiones, Quejas, Reclamos,
                    Sugerencias, Denuncias, Felicitaciones - PQRSDF
                </Typography>
                <Typography
                    textAlign="right"
                    mt="-.8rem"
                    mb="1rem"
                    fontSize=".88rem"
                >
                    <span style={{ color: 'red' }}>*</span>
                    Campos obligatorios
                </Typography>
                <hr />
                <Grid container pt="1rem">
                    <Grid item xs={12} md={6}>
                        <Typography fontSize="1.5rem">
                            Datos solicitante
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6} textAlign="right">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    className="checkbox_boot"
                                    id="policy"
                                    name="aceptoPolitica"
                                    checked={isAnonymous}
                                    onChange={handleChangeAnonymous}
                                    onBlur={formik.handleBlur}
                                />
                            }
                            label="Usuario anónimo"
                            labelPlacement="start"
                        />
                        <AnonymousDialog
                            open={showAnonymousDialog}
                            onClose={handleCloseAnonymousDialog}
                            onAccept={handleAceptAnonymousDialog}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Form
                formik={formik}
                identifications={identifications}
                countries={countries}
                departments={departments}
                cities={cities}
                requestTypes={requestTypesFiltered.map(({ id, name, showInAnonymous }: any) => ({ id, name, showInAnonymous }))}
                requestClassifications={requestClassificationsFiltered.map(({ id, name }: any) => ({ id, name }))}
                onChangeFiles={onChangeFiles}
                load={load}
                disabledBtn={!isFormValid() || load}
                onChangeShowPrivacy={(show: boolean) => setShowPrivacy(show)}
                showPrivacy={showPrivacy}
                isAnonymous={isAnonymous}
            />
        </Box>
    );
};

export default home;
